import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import {
  DEFAULT_APPEARANCE,
  DEFAULT_FILL_COLORS,
  appearanceCssVars,
  appearanceSettingsSchema,
  cellAlignCss,
  type AppearanceSettings,
  type CellAlignSettings,
  type CitizenshipColors,
  type ColumnHeaderSettings,
  type FillColorSetting,
  type GridLineSettings,
} from '@yana/shared';
import { api } from '@/api/client';

/**
 * Общий вид таблицы: цвет и толщина линий сетки, насыщенность заголовков,
 * выравнивание ячеек.
 *
 * Настройка одна на всех и лежит в базе, но применяться обязана до первой
 * отрисовки. Ждать ответа сервера нельзя: таблица успевает нарисоваться
 * раньше, и линии на каждой загрузке прыгали бы с умолчания на настроенное.
 *
 * Отсюда кэш в localStorage и inline-скрипт в index.html, который его
 * применяет, — ровно тот же приём, что у темы в stores/theme.ts. Ответ
 * сервера потом молча уточняет картину.
 */

const STORAGE_KEY = 'yana.appearance';

/**
 * Готовые значения переменных — отдельным ключом.
 *
 * Inline-скрипт в index.html читает именно его, а не саму настройку:
 * пресеты живут в @yana/shared, и повторять их в скрипте без сборщика
 * значило бы завести вторую копию, которая однажды разойдётся с первой.
 */
const CSS_STORAGE_KEY = 'yana.appearance.css';

/** Те же имена ключей читает inline-скрипт в index.html — менять их вместе. */
export const APPEARANCE_STORAGE_KEY = STORAGE_KEY;
export const APPEARANCE_CSS_STORAGE_KEY = CSS_STORAGE_KEY;

function readCached(): AppearanceSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_APPEARANCE;
    const parsed = appearanceSettingsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_APPEARANCE;
  } catch {
    // localStorage закрыт политикой или в нём мусор — рисуем по умолчанию
    return DEFAULT_APPEARANCE;
  }
}

/**
 * Переменные ставятся на корень, а не на грид: их читают и превью на
 * экране настроек, и полоса группировки, и шапка — все внутри разных
 * поддеревьев.
 *
 * Ставятся все разом, а не по разделу: раздел, за которым здесь не следят,
 * сохраняется в базу и в кэш, но на экране появляется только после
 * перезагрузки страницы — и выглядит это как «настройка не работает».
 */
export function applyAppearanceCss(settings: AppearanceSettings): void {
  const root = document.documentElement;
  const vars = appearanceCssVars(settings);
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  applyCellAlignCss(settings);
  try {
    localStorage.setItem(CSS_STORAGE_KEY, JSON.stringify(vars));
  } catch {
    // Не запомнили — следующая загрузка просто начнётся с умолчания
  }
}

/** Свой элемент, а не общий с чем-то ещё: он переписывается целиком. */
const ALIGN_STYLE_ID = 'grid-cell-align';

/**
 * Выравнивание — таблицей стилей, а не переменными на корне.
 *
 * У каждой колонки оно своё, и сорок одно значение в одну переменную
 * на `:root` не кладётся. Правило по колонке ставит переменные ей,
 * а `justify-content` и `align-items` читают их в `.grid-cell-align`
 * одним объявлением на всю таблицу: ячейкам не достаётся ни класса,
 * ни стиля, и смена настройки не пересчитывает ни одну из восьмисот
 * видимых.
 *
 * В inline-скрипт index.html это не уехало, в отличие от линий, и это
 * не забывчивость: скрипт нужен тем переменным, которые видны до запуска
 * приложения, а таблица рисуется уже после. Стор поднимается из кэша
 * синхронно, и правила встают раньше первой ячейки.
 */
export function applyCellAlignCss(settings: AppearanceSettings): void {
  let el = document.getElementById(ALIGN_STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = ALIGN_STYLE_ID;
    document.head.append(el);
  }
  el.textContent = cellAlignCss(settings.cellAlign);
}

export const useAppearanceStore = defineStore('appearance', () => {
  const settings = ref<AppearanceSettings>(readCached());
  const loaded = ref(false);
  const saving = ref(false);

  watch(settings, applyAppearanceCss, { immediate: true, deep: true });

  watch(
    settings,
    (value) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch {
        // Не смогли запомнить — переживём, просто следующая загрузка
        // начнётся с умолчания и уточнится ответом сервера
      }
    },
    { deep: true },
  );

  /**
   * Загрузка идёт один раз за жизнь страницы: настройка меняется раз в
   * полгода, а зовут её с каждого экрана.
   */
  let loadingPromise: Promise<void> | null = null;
  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return;
    if (loadingPromise) return loadingPromise;
    loadingPromise = api.get<{ appearance?: AppearanceSettings }>('/settings')
      .then((res) => {
        if (res.appearance) settings.value = appearanceSettingsSchema.parse(res.appearance);
        // До ответа показываем кэш, но сохранять его поверх общей палитры нельзя.
        loaded.value = true;
      })
      .catch(() => {
        // Настройки не пришли — таблица рисуется по кэшу или по умолчанию.
        loaded.value = false;
      })
      .finally(() => { loadingPromise = null; });
    await loadingPromise;
  }

  /**
   * Раздел отправляется вместе с остальными, а не в одиночку.
   *
   * `PUT /settings/appearance` принимает настройку целиком, и у пропущенных
   * разделов схема подставит умолчание. Отправить одни линии означало бы
   * заодно вернуть цвета гражданства к заводским — молча и на глазах
   * у того, кто их только что выставил.
   *
   * Ответ разбирается схемой, а не кладётся как есть, — и это не
   * перестраховка. Сервер обновляется отдельно от страницы: пока на нём
   * версия постарше, раздела, которого она не знает, в ответе не будет
   * вовсе — её схема его отбросит. Присвоенный как есть, такой ответ
   * ронял весь экран настроек: «Cannot read properties of undefined».
   */
  async function save(patch: Partial<AppearanceSettings>): Promise<void> {
    saving.value = true;
    try {
      const body: AppearanceSettings = { ...settings.value, ...patch };
      const res = await api.put<{ appearance: AppearanceSettings }>('/settings/appearance', body);
      settings.value = appearanceSettingsSchema.parse(res.appearance);
    } finally {
      saving.value = false;
    }
  }

  const saveGridLines = (next: GridLineSettings) => save({ gridLines: next });
  const saveCitizenshipColors = (next: CitizenshipColors) => save({ citizenshipColors: next });
  const saveFillColors = (next: FillColorSetting[]) => save({ fillColors: next, fillColorsConfigured: true });
  const saveColumnHeader = (next: ColumnHeaderSettings) => save({ columnHeader: next });
  const saveCellAlign = (next: CellAlignSettings) => save({ cellAlign: next });

  /**
   * Палитра заливки ячеек.
   *
   * Пока настройки не пришли, отдаёт заводскую: меню ячейки открывается
   * раньше, чем успевает ответить сервер, и пустая полоса вместо цветов
   * выглядела бы поломкой.
   */
  const fillColors = computed<readonly FillColorSetting[]>(
    () => settings.value.fillColors ?? DEFAULT_FILL_COLORS,
  );

  /**
   * Настройку поменял кто-то другой — пришло событием.
   *
   * Разбирается схемой, а не кладётся как есть: событие приходит с сервера,
   * который обновляется отдельно от страницы, и раздела, которого эта
   * версия не знает, в нём может не быть вовсе. Присвоенный как есть,
   * такой ответ ронял экран настроек целиком.
   *
   * Ничего не спрашивает и ничего не показывает: вид таблицы общий,
   * и это ровно то поведение, которого ждёт человек, которому сказали
   * «я поменял цвета» — он смотрит в таблицу и видит новые.
   */
  function applyRemote(next: AppearanceSettings): void {
    const parsed = appearanceSettingsSchema.safeParse(next);
    if (parsed.success) settings.value = parsed.data;
  }

  /** Сбросить локальное состояние — нужно при выходе из учётной записи */
  function reset(): void {
    loaded.value = false;
  }

  return {
    settings,
    loaded,
    saving,
    fillColors,
    ensureLoaded,
    saveGridLines,
    saveCitizenshipColors,
    saveFillColors,
    saveColumnHeader,
    saveCellAlign,
    /**
     * Сохранить несколько разделов разом.
     *
     * Экран настроек шлёт все пять одним запросом: `PUT /settings/appearance`
     * и так принимает настройку целиком, а пять запросов подряд означали бы
     * пять записей в журнале и пять шансов разойтись на полпути.
     */
    saveSections: save,
    applyRemote,
    reset,
  };
});
